import { Button, Editor, Input, Label, LoadingModal, Modal, Select } from "@mixitone/components";
import type { FundingOutreach } from "@mixitone/models";
import { ApplicationView } from "@mixitone/mvc";
import { FUNDING_TEMPLATE_VARIABLES } from "@mixitone/util";
import { FundingOutreachFormController } from "components/Pages/Funding/FundingOutreachFormController";

/** Modal to compose and send an outreach email. */
const FundingOutreachComposeModal = () => {
  const controller = FundingOutreachFormController.use();
  const {
    showComposeModal,
    composeSubject,
    composeBody,
    composeMergeValues,
    sendingEmail,
    record,
    emailTemplatesForCompose,
    composeFundingAppealEmailTemplateId,
  } = controller.state;
  const funder = (record as FundingOutreach | null)?.funder;
  const contactEmail = funder?.contact_email?.trim();

  if (!showComposeModal) return null;

  const handleClose = () => controller.actionCloseCompose();
  const handleSend = async () => {
    if (!composeSubject.trim() || !composeBody.trim()) return;
    const result = await controller.actionSendEmail(composeSubject.trim(), composeBody.trim());
    controller.actionCloseCompose();
    if (result?.pendingReview) controller.actionSetPendingReviewMessage(true);
  };

  return (
    <Modal
      title="Send email"
      className="flex h-[85vh] max-h-[85vh] w-full min-w-[85vw] max-w-2xl flex-col overflow-hidden sm:h-[85vh] sm:max-h-[85vh] sm:min-w-[85vw] sm:w-full"
      onClose={handleClose}
      portal
    >
      {sendingEmail ? (
        <LoadingModal title="Sending…" />
      ) : (
        <div className="flex min-h-0 flex-1 flex-col gap-4">
          <div className="shrink-0">
            <Label>To</Label>
            <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
              {funder?.name ?? ""} &lt;{contactEmail}&gt;
            </div>
          </div>
          <div className="shrink-0">
            <Label htmlFor="compose-subject">Subject</Label>
            <Input
              id="compose-subject"
              value={composeSubject}
              onChange={(e) => controller.actionSetComposeSubject(e.target.value)}
              placeholder="Funding request – …"
              className="w-full"
            />
          </div>
          <div className="shrink-0">
            <Label htmlFor="compose-template-source">Template</Label>
            <Select
              id="compose-template-source"
              value={composeFundingAppealEmailTemplateId ?? "__custom__"}
              onChange={(e) => {
                const v = e.target.value as string;
                controller.actionSetComposeTemplateSource(v === "__custom__" ? "__custom__" : v);
              }}
              className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
              aria-label="Email template source"
            >
              {emailTemplatesForCompose.map((t) => (
                <option key={t.id} value={t.id!}>
                  {t.name ?? "Untitled"}
                </option>
              ))}
              <option value="__custom__">Custom</option>
            </Select>
            <p className="mt-1 text-xs text-gray-500">
              Using a saved template links this send so the template locks after a successful send.
            </p>
          </div>
          <div className="flex min-h-0 flex-1 flex-col">
            <Label className="shrink-0">Message</Label>
            <div className="min-h-0 flex-1 overflow-y-auto rounded border border-gray-200">
              <Editor
                value={composeBody}
                onChange={(e) => controller.actionSetComposeBody(e.target.value)}
                className="min-h-[250px] w-full border-0"
                insertableVariables={FUNDING_TEMPLATE_VARIABLES.map((variable) => ({
                  label: `${variable.label} (${composeMergeValues[variable.key] ?? ""})`,
                  token: composeMergeValues[variable.key] ?? "",
                }))}
              />
            </div>
          </div>
          <div className="flex shrink-0 justify-end gap-2">
            <Button kind="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              kind="primary"
              onClick={handleSend}
              disabled={sendingEmail || !composeSubject.trim() || !composeBody.trim()}
              spinner={sendingEmail}
            >
              Send email
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ApplicationView(FundingOutreachComposeModal);
